/**
 * Pure document transforms. Every edit goes through one of these, so undo/redo
 * only has to keep the returned snapshots — no mutation happens in place.
 */

import type { Band, Category, CategoryId, ChronoDocument, Item } from './types';

const replaceAt = <T>(list: readonly T[], index: number, value: T): T[] =>
  list.map((current, i) => (i === index ? value : current));

const removeAt = <T>(list: readonly T[], index: number): T[] =>
  list.filter((_, i) => i !== index);

function swap<T>(list: readonly T[], a: number, b: number): T[] {
  if (a < 0 || b < 0 || a >= list.length || b >= list.length) return [...list];
  const next = [...list];
  const first = next[a] as T;
  next[a] = next[b] as T;
  next[b] = first;
  return next;
}

/* ------------------------------------------------------------------- entries */

export function addItem(doc: ChronoDocument, item: Item): ChronoDocument {
  return { ...doc, items: [...doc.items, item] };
}

export function updateItem(doc: ChronoDocument, index: number, item: Item): ChronoDocument {
  return { ...doc, items: replaceAt(doc.items, index, item) };
}

export function removeItem(doc: ChronoDocument, index: number): ChronoDocument {
  return { ...doc, items: removeAt(doc.items, index) };
}

/**
 * Pins an entry to a lane. The caller passes the lane the entry currently
 * occupies, because until it is pinned that lane is computed, not stored.
 */
export function pinItemLane(doc: ChronoDocument, index: number, lane: number): ChronoDocument {
  const item = doc.items[index];
  if (!item) return doc;
  return updateItem(doc, index, { ...item, lane: Math.max(0, Math.trunc(lane)) });
}

/** Hands the entry back to automatic stacking. */
export function unpinItemLane(doc: ChronoDocument, index: number): ChronoDocument {
  const item = doc.items[index];
  if (!item || item.lane === undefined) return doc;

  const next = { ...item };
  delete next.lane;
  return updateItem(doc, index, next);
}

/* --------------------------------------------------------------------- bands */

export function addBand(doc: ChronoDocument, band: Band): ChronoDocument {
  return { ...doc, bands: [...doc.bands, band] };
}

export function updateBand(doc: ChronoDocument, index: number, band: Band): ChronoDocument {
  return { ...doc, bands: replaceAt(doc.bands, index, band) };
}

export function removeBand(doc: ChronoDocument, index: number): ChronoDocument {
  return { ...doc, bands: removeAt(doc.bands, index) };
}

export function moveBand(doc: ChronoDocument, index: number, delta: number): ChronoDocument {
  return { ...doc, bands: swap(doc.bands, index, index + delta) };
}

/* ---------------------------------------------------------------- categories */

/** Slug derived from the name, with a numeric suffix if it is already taken. */
export function categoryId(name: string, existing: Readonly<Record<string, unknown>>): CategoryId {
  const base =
    name
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'categorie';

  if (!(base in existing)) return base;

  let suffix = 2;
  while (`${base}-${suffix}` in existing) suffix += 1;
  return `${base}-${suffix}`;
}

export function addCategory(
  doc: ChronoDocument,
  id: CategoryId,
  category: Category,
): ChronoDocument {
  return { ...doc, categories: { ...doc.categories, [id]: category } };
}

export function updateCategory(
  doc: ChronoDocument,
  id: CategoryId,
  category: Category,
): ChronoDocument {
  if (!(id in doc.categories)) return doc;
  return { ...doc, categories: { ...doc.categories, [id]: category } };
}

export function isCategoryUsed(doc: ChronoDocument, id: CategoryId): boolean {
  return doc.items.some((item) => item.cat === id);
}

/** Refuses to orphan entries: a category in use must be emptied first. */
export function removeCategory(doc: ChronoDocument, id: CategoryId): ChronoDocument {
  if (isCategoryUsed(doc, id)) return doc;

  const categories = { ...doc.categories };
  delete categories[id];
  return { ...doc, categories };
}
